import React, { useState, useMemo, useCallback } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useBoolean } from 'react-use'

import { getResourceNameFromResponseBody } from 'components/services/create/TestEvaluationResultsStatusCheckIntegration'
import { useIsDynatraceEnhancedStatusCheck } from 'components/status-checks/hooks/use-is-dynatrace-enhanced-status-check'
import {
  isDynatraceSelectorEntityIdType,
  isDynatraceSelectorK8sQueryType
} from 'components/status-checks/status-checks-dynatrace-form-utils'
import { type EndpointResponse } from 'types/api/status-checks/status-check'

import type { StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'

export interface StatusCheckDetailsContextValues {
  endpointResponse: EndpointResponse | undefined
  isStatusCheckConnectionValidated: boolean
  isStatusCheckEvaluationValidated: boolean
  isTestStatusCheckDisabled: boolean
  isTestSuccessEvaluationDisabled: boolean
  serviceName?: string
  shouldDisplayPredicateConfiguration: boolean
  handleSuccess: (endpointResponse: EndpointResponse) => Promise<void>
  handleTestConnectionError: () => void
  setEndpointResponse: (endpointResponse: EndpointResponse | undefined) => void
  setIsStatusCheckConnectionValidated: (arg: boolean) => void
  setIsStatusCheckEvaluationValidated: (arg: boolean) => void
}

export const StatusCheckDetailsContext = React.createContext<
  StatusCheckDetailsContextValues | undefined
>(undefined)

const fieldsToWatch = [
  'dynatraceSelector',
  'evaluationConfiguration.responseBodyEvaluation.predicates',
  'externalIntegration',
  'monitorUrl',
  'statusCheckName'
] as const

export const StatusCheckDetailsProvider: React.FC<{
  serviceName?: string
}> = ({ children, serviceName = '' }) => {
  const [
    isStatusCheckEvaluationValidated,
    setIsStatusCheckEvaluationValidated
  ] = useBoolean(false)

  const [
    isStatusCheckConnectionValidated,
    setIsStatusCheckConnectionValidated
  ] = useBoolean(false)

  const isDynatraceEnhancedStatusCheck = useIsDynatraceEnhancedStatusCheck()

  const [endpointResponse, setEndpointResponse] = useState<
    EndpointResponse | undefined
  >()

  const { formState, getFieldState, setValue, trigger } =
    useFormContext<StatusCheckFormValues>()

  const [
    dynatraceSelector,
    predicates,
    externalIntegration,
    monitorUrl,
    statusCheckName
  ] = useWatch<StatusCheckFormValues, typeof fieldsToWatch>({
    name: fieldsToWatch
  })

  const dynatraceEntityIdState = getFieldState('dynatraceSelector.entityId')
  const dynatraceK8sQueryState = getFieldState('dynatraceSelector.k8sQuery')
  const monitorUrlState = getFieldState('monitorUrl')

  // Field validation flags to ensure 'test status check' action is enabled
  const isDynatraceEntityIdValid =
    isDynatraceSelectorEntityIdType(dynatraceSelector) &&
    Boolean(dynatraceSelector.entityId) &&
    !dynatraceEntityIdState.error
  const isDynatraceK8sQueryValid =
    isDynatraceSelectorK8sQueryType(dynatraceSelector) &&
    Boolean(dynatraceSelector.k8sQuery) &&
    !dynatraceK8sQueryState.error
  const isMonitorUrlValid = Boolean(monitorUrl) && !monitorUrlState.error

  const isTestStatusCheckDisabled =
    // Default check here ensures the monitor URL field is valid
    (!isDynatraceEnhancedStatusCheck && !isMonitorUrlValid) ||
    // In the case of Dynatrace enhanced status checks, we must instead check for either a valid
    // 'entityId' or 'k8sQuery' field value.
    (isDynatraceEnhancedStatusCheck &&
      !isDynatraceEntityIdValid &&
      !isDynatraceK8sQueryValid)

  const isTestSuccessEvaluationDisabled =
    isTestStatusCheckDisabled ||
    !isStatusCheckConnectionValidated ||
    !endpointResponse ||
    Boolean(formState.errors?.evaluationConfiguration?.requestTimeout)

  const shouldDisplayPredicateConfiguration = useMemo<boolean>(() => {
    if (predicates && !endpointResponse) {
      return predicates.length > 0
    }

    const { contentType } = endpointResponse ?? {}
    // We can probably assume here that we lead with '/', but cannot guarantee that this is
    // always placed at the end of the string. Example: PNI responses might be semi-colon delimited.
    const isJsonPattern = /\/json/i
    const isJsonContentType = contentType
      ? isJsonPattern.test(contentType)
      : false

    return isJsonContentType
  }, [endpointResponse, predicates])

  const handleSuccess = useCallback(
    async (endpointResponse: EndpointResponse) => {
      setEndpointResponse(endpointResponse)
      setIsStatusCheckConnectionValidated(true)
      setIsStatusCheckEvaluationValidated(false)

      // Pull the resource name from the response body
      const resourceName = externalIntegration
        ? getResourceNameFromResponseBody(endpointResponse, externalIntegration)
        : undefined

      // don't overwrite the status check name from the response body if it exists
      // in the form
      if (resourceName && !statusCheckName) {
        setValue('statusCheckName', resourceName)
      }

      // Validate monitorUrl on test success
      await trigger('monitorUrl')
    },
    [
      externalIntegration,
      statusCheckName,
      setIsStatusCheckConnectionValidated,
      setIsStatusCheckEvaluationValidated,
      setValue,
      trigger
    ]
  )

  const handleTestConnectionError = useCallback(() => {
    if (isStatusCheckConnectionValidated) {
      setIsStatusCheckConnectionValidated(false)
    }
  }, [isStatusCheckConnectionValidated, setIsStatusCheckConnectionValidated])

  const value = useMemo(() => {
    return {
      endpointResponse,
      isStatusCheckConnectionValidated,
      isStatusCheckEvaluationValidated,
      isTestStatusCheckDisabled,
      isTestSuccessEvaluationDisabled,
      serviceName,
      shouldDisplayPredicateConfiguration,
      handleSuccess,
      handleTestConnectionError,
      setEndpointResponse,
      setIsStatusCheckConnectionValidated,
      setIsStatusCheckEvaluationValidated
    }
  }, [
    endpointResponse,
    isStatusCheckConnectionValidated,
    isStatusCheckEvaluationValidated,
    isTestStatusCheckDisabled,
    isTestSuccessEvaluationDisabled,
    serviceName,
    shouldDisplayPredicateConfiguration,
    handleSuccess,
    handleTestConnectionError,
    setIsStatusCheckConnectionValidated,
    setIsStatusCheckEvaluationValidated
  ])

  return (
    <StatusCheckDetailsContext.Provider value={value}>
      {children}
    </StatusCheckDetailsContext.Provider>
  )
}

export function useStatusCheckDetailsContext() {
  const context = React.useContext(StatusCheckDetailsContext)

  if (context === undefined) {
    throw new Error(
      'useStatusCheckDetailsContext must be used within a StatusCheckDetailsProvider'
    )
  }

  return context
}
